import React from 'react';
import styled from 'styled-components';

import { useTypedSelector } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { DEFAULT_TIMEZONE, zoneOffset } from '../configStudio/configModel';
import { ConfigStepPanel, TC } from '../configStudio/kit/ConfigPanel';
import { DayTimeFields, LimitLine } from '../configStudio/kit/configFields';
import { Checkbox } from '../wizard/kit/controls';
import { DateField } from '../wizard/kit/fields';
import { Divider, Form, Line, Words } from '../wizard/kit/layout';
import { wz, wzType } from '../wizard/wizardStyles';
import { ruleStepErrors } from './ruleAdapter';
import { IRuleStepProps, RULE_STEP_TOTAL, RuleLimits } from './ruleModel';

const TS = `${TC}.rule.schedule`;

const SubQuestion = styled.h3`
  margin: 0 0 8px;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.question}
`;

// 6985:10720: the four limits in a 2 x 2 grid.
const LimitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 24px;
`;

/** 54 Step 4/4: when the rule runs, then its limits; "Finalise" goes to the review. */
const RuleScheduleStep: React.FC<IRuleStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const timezone = useTypedSelector(
    store => store.global?.tenantConfig?.localization?.timezone ?? DEFAULT_TIMEZONE
  );
  const { limits } = state;
  const setLimits = (patch: Partial<RuleLimits>) =>
    update(current => ({ ...current, limits: { ...current.limits, ...patch } }));

  return (
    <ConfigStepPanel
      title={t(`${TC}.rule.title`)}
      step={4}
      total={RULE_STEP_TOTAL}
      question={t(`${TS}.question`)}
      errors={ruleStepErrors(state, t)}
      onNext={next}
      onBack={back}
      last
      testId="orbit-rule-step-schedule"
    >
      <Form>
        <Line>
          <Words>{t(`${TS}.start`)}</Words>
          <DateField
            label={t(`${TS}.start`)}
            value={state.beginsAt}
            onChange={beginsAt => update(current => ({ ...current, beginsAt }))}
          />
        </Line>
        <Line>
          <Checkbox
            checked={state.endsOn}
            label={t(`${TS}.end`)}
            onChange={endsOn => update(current => ({ ...current, endsOn }))}
          />
          <Words $muted={!state.endsOn}>{t(`${TS}.end`)}</Words>
          <DateField
            label={t(`${TS}.endPlaceholder`)}
            withTime
            value={state.endsAt}
            disabled={!state.endsOn}
            onChange={endsAt => update(current => ({ ...current, endsAt }))}
          />
        </Line>
        <DayTimeFields
          label={t(`${TS}.specific`)}
          value={state.window}
          onChange={window => update(current => ({ ...current, window }))}
          zoneLabel={t(`${TC}.tenantTimezone`, { offset: zoneOffset(timezone) })}
          rangeLabels="fromTo"
        />
        <Divider />
        <SubQuestion>{t(`${TS}.limits`)}</SubQuestion>
        <LimitGrid>
          <LimitLine
            label={t(`${TS}.total`)}
            unit={t(`${TS}.totalUnit`)}
            on={limits.totalOn}
            value={limits.total}
            onToggle={totalOn => setLimits({ totalOn })}
            onValue={total => setLimits({ total })}
          />
          <LimitLine
            label={t(`${TS}.perPerson`)}
            unit={t(`${TS}.perPersonUnit`)}
            on={limits.perPersonOn}
            value={limits.perPerson}
            onToggle={perPersonOn => setLimits({ perPersonOn })}
            onValue={perPerson => setLimits({ perPerson })}
          />
          <LimitLine
            label={t(`${TS}.rate`)}
            unit={t(`${TS}.rateUnit`)}
            on={limits.rateOn}
            value={limits.rate}
            period={limits.ratePeriod}
            onToggle={rateOn => setLimits({ rateOn })}
            onValue={rate => setLimits({ rate })}
            onPeriod={ratePeriod => setLimits({ ratePeriod })}
          />
          <LimitLine
            label={t(`${TS}.userRate`)}
            unit={t(`${TS}.userRateUnit`)}
            on={limits.userRateOn}
            value={limits.userRate}
            period={limits.userRatePeriod}
            onToggle={userRateOn => setLimits({ userRateOn })}
            onValue={userRate => setLimits({ userRate })}
            onPeriod={userRatePeriod => setLimits({ userRatePeriod })}
          />
        </LimitGrid>
      </Form>
    </ConfigStepPanel>
  );
};

export default RuleScheduleStep;
